/**
 * Label, hint and error, wired to the control automatically.
 *
 * `Field` puts an id, `aria-describedby` and `aria-invalid` into context. The
 * controls in `Input.tsx` read that context, so this is all a page has to write:
 *
 * ```tsx
 * <Field label="Search" hint="Name, company or email" error={error}>
 *   <Input value={q} onChange={...} />
 * </Field>
 * ```
 */
import type { LabelHTMLAttributes, ReactNode } from 'react'
import { createContext, useContext, useId } from 'react'
import { cn } from '../lib/cn'

export interface FieldContextValue {
  id: string
  describedBy: string | undefined
  invalid: boolean
  required: boolean
  disabled: boolean
}

const FieldContext = createContext<FieldContextValue | null>(null)

export function useFieldContext(): FieldContextValue | null {
  return useContext(FieldContext)
}

export interface LabelProps extends LabelHTMLAttributes<HTMLLabelElement> {
  required?: boolean
}

export function Label({ children, className, required, ...rest }: LabelProps) {
  return (
    <label className={cn('block text-base font-medium text-ink-secondary', className)} {...rest}>
      {children}
      {required ? (
        <span className="ml-0.5 text-danger" aria-hidden="true">
          *
        </span>
      ) : null}
    </label>
  )
}

export interface FieldProps {
  label: ReactNode
  children: ReactNode
  /** Explains the field before it goes wrong. */
  hint?: ReactNode
  /** A message means the field is invalid. */
  error?: ReactNode
  required?: boolean
  disabled?: boolean
  /** Hide the label visually while keeping it for screen readers. */
  labelHidden?: boolean
  className?: string
  /** Extra content on the right of the label row, such as a character counter. */
  aside?: ReactNode
}

export function Field({
  label,
  children,
  hint,
  error,
  required = false,
  disabled = false,
  labelHidden = false,
  className,
  aside,
}: FieldProps) {
  const id = useId()
  const hintId = hint ? `${id}-hint` : undefined
  const errorId = error ? `${id}-error` : undefined
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined

  return (
    <FieldContext.Provider value={{ id, describedBy, invalid: Boolean(error), required, disabled }}>
      <div className={cn('space-y-1.5', className)}>
        <div className={cn('flex items-baseline justify-between gap-3', labelHidden && 'sr-only')}>
          <Label htmlFor={id} required={required}>
            {label}
          </Label>
          {aside ? <span className="text-xs text-ink-subtle">{aside}</span> : null}
        </div>
        {children}
        {hint && !error ? (
          <p id={hintId} className="text-base text-ink-muted">
            {hint}
          </p>
        ) : null}
        {error ? (
          <p id={errorId} className="text-base text-danger">
            {error}
          </p>
        ) : null}
      </div>
    </FieldContext.Provider>
  )
}

/**
 * A group of related controls that share one label, such as a radio set.
 *
 * `layout="side"` is the builder settings shape: the section's name and a
 * sentence about it in a 280px column on the left, the controls in a column
 * of up to 752 on the right. Below 1024 the two stack, because a 280px
 * label column on a phone leaves the controls no room at all.
 */
export function Fieldset({
  legend,
  children,
  hint,
  error,
  className,
  layout = 'stacked',
  description,
}: {
  legend: ReactNode
  children: ReactNode
  hint?: ReactNode
  error?: ReactNode
  className?: string
  layout?: 'stacked' | 'side'
  /** The sentence under the legend in the side layout. */
  description?: ReactNode
}) {
  const legendId = useId()
  if (layout === 'side') {
    // A legend is only a legend as the fieldset's first child, and a browser
    // will not lay one out as a grid item. So the side layout names the group
    // through `aria-labelledby` and draws the name as an ordinary heading.
    return (
      <fieldset
        aria-labelledby={legendId}
        className={cn(
          'grid grid-cols-1 gap-4 lg:grid-cols-[280px_minmax(0,752px)] lg:gap-8',
          className,
        )}
      >
        <div className="min-w-0">
          <p id={legendId} className="text-base font-semibold text-ink-secondary">
            {legend}
          </p>
          {description ? <p className="text-base text-ink-muted">{description}</p> : null}
        </div>
        <div className="flex min-w-0 flex-col gap-4">
          {hint && !error ? <p className="text-base text-ink-muted">{hint}</p> : null}
          {children}
          {error ? <p className="text-base text-danger">{error}</p> : null}
        </div>
      </fieldset>
    )
  }

  return (
    <fieldset className={cn('space-y-2', className)}>
      <legend className="text-base font-medium text-ink-secondary">{legend}</legend>
      {hint && !error ? <p className="text-base text-ink-muted">{hint}</p> : null}
      {children}
      {error ? <p className="text-base text-danger">{error}</p> : null}
    </fieldset>
  )
}
